/**
 * React Query hooks for Promotion admin API.
 *
 * All query keys include tenant to prevent cross-tenant cache collisions.
 * All queries are disabled until tenant is available (auth hydration).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/lib/store/authStore";
import { toast } from "sonner";
import { promotionApi } from "./promotionApi";
import type {
  PromotionListResponse,
  PromotionDetail,
  CreatePromotionInput,
  UpdatePromotionInput,
  CouponAnalyticsResponse,
} from "./promotionTypes";

function useTenant() {
  return useAuthStore((s) => s.currentTenant);
}

export const promotionQueryKeys = {
  all: (tenant: string | null) => ["promotions", tenant] as const,
  list: (tenant: string | null, page: number) =>
    ["promotions", "list", tenant, page] as const,
  detail: (tenant: string | null, couponCode: string) =>
    ["promotions", "detail", tenant, couponCode] as const,
  analytics: (tenant: string | null) =>
    ["promotions", "analytics", tenant] as const,
};

export function usePromotionList(page = 0, pageLength = 20) {
  const tenant = useTenant();

  return useQuery<PromotionListResponse>({
    queryKey: promotionQueryKeys.list(tenant, page),
    queryFn: () => promotionApi.list(page * pageLength, pageLength),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  });
}

export function usePromotionDetail(couponCode: string) {
  const tenant = useTenant();

  return useQuery<PromotionDetail>({
    queryKey: promotionQueryKeys.detail(tenant, couponCode),
    queryFn: () => promotionApi.detail(couponCode),
    enabled: !!tenant && !!couponCode,
  });
}

export function useCreatePromotion() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tenant = useTenant();

  return useMutation({
    mutationFn: (data: CreatePromotionInput) => promotionApi.create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: promotionQueryKeys.all(tenant),
      });
      toast.success("Promotion created!");
      navigate(`/promotions/${encodeURIComponent(result.coupon_code)}`);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create promotion");
    },
  });
}

export function useUpdatePromotion(couponCode: string) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tenant = useTenant();

  return useMutation({
    mutationFn: (data: UpdatePromotionInput) =>
      promotionApi.update(couponCode, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: promotionQueryKeys.all(tenant),
      });
      toast.success("Promotion updated!");
      navigate(`/promotions/${encodeURIComponent(couponCode)}`);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update promotion");
    },
  });
}

export function useEndPromotion(couponCode: string) {
  const queryClient = useQueryClient();
  const tenant = useTenant();

  return useMutation({
    mutationFn: () => promotionApi.end(couponCode),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: promotionQueryKeys.all(tenant),
      });
      toast.success("Promotion ended");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to end promotion");
    },
  });
}

export function useIssueCouponToGroupNow(couponCode: string) {
  const queryClient = useQueryClient();
  const tenant = useTenant();

  return useMutation({
    mutationFn: (company: string) =>
      promotionApi.issueCouponToGroupNow(couponCode, company),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: promotionQueryKeys.detail(tenant, couponCode),
      });
      toast.success(`Issued to ${result.issued_count} customer(s)`);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to issue coupon to group");
    },
  });
}

export function useCouponAnalytics() {
  const tenant = useTenant();

  return useQuery<CouponAnalyticsResponse>({
    queryKey: promotionQueryKeys.analytics(tenant),
    queryFn: () => promotionApi.analytics(),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  });
}
